/* eslint-disable react/no-array-index-key */
import { PlusIcon } from "@heroicons/react/20/solid";
import { cloneDeep } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "../../../../../components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../../components/ui/popover";
import { filterIncompletePredicates } from "../../../../../shared/filter/utils/filterValidation";
import { getEntityLabel } from "../../../../../shared/filter/utils/entityDisplayUtils";
import SignalFilterContainer from "./SignalFilterContainer";

type Props = {
  filters: any;
  entityRelation: string;
  onApply: (newFilters: any) => void;
  target: React.ReactNode;
  width?: number;
  position?: "bottom" | "bottom-start" | "bottom-end";
};

const getPopoverAlignment = (position: "bottom" | "bottom-start" | "bottom-end") => {
  if (position === "bottom-end") return "end";
  if (position === "bottom-start") return "start";
  return "center";
};

const EMPTY_CONDITION = {
  field: "",
  operator: "",
  value: "",
  type: "",
  entity_type: "column",
};

const createEmptyGroup = () => ({
  predicates: [{ ...EMPTY_CONDITION }],
});

const ensureEditableFilters = (filters: any) => {
  const source = cloneDeep(filters && typeof filters === "object" ? filters : {});
  const groups = Array.isArray(source?.predicates) ? source.predicates : [];
  const normalizedGroups = groups.length
    ? groups.map((group: any) => ({
      ...group,
      predicates: Array.isArray(group?.predicates) && group.predicates.length
        ? group.predicates
        : [{ ...EMPTY_CONDITION }],
    }))
    : [createEmptyGroup()];

  return {
    ...source,
    predicates: normalizedGroups,
  };
};

const sanitizePopoverFilters = (filters: any) => {
  const source = filters && typeof filters === "object" ? filters : {};
  const groups = (Array.isArray(source?.predicates) ? source.predicates : [])
    .map((group: any) => {
      const predicates = filterIncompletePredicates(group?.predicates || []);
      if (!predicates.length) return null;
      return {
        ...group,
        predicates,
      };
    })
    .filter(Boolean);

  if (!groups.length) {
    return {};
  }

  return {
    ...source,
    query_type: String(source?.query_type || "AND").toUpperCase() === "OR" ? "OR" : "AND",
    predicates: groups,
  };
};

const SignalFilterPopover = ({
  filters,
  entityRelation,
  onApply,
  target,
  width = 620,
  position = "bottom-start",
}: Props) => {
  const [opened, setOpened] = useState(false);
  const [localFilters, setLocalFilters] = useState<any>(ensureEditableFilters(filters));
  const [baselineSnapshot, setBaselineSnapshot] = useState("{}");

  useEffect(() => {
    if (!opened) return;
    const editableFilters = ensureEditableFilters(filters);
    setLocalFilters(editableFilters);
    setBaselineSnapshot(JSON.stringify(sanitizePopoverFilters(editableFilters)));
  }, [opened, filters]);

  const sanitizedLocalFilters = useMemo(
    () => sanitizePopoverFilters(localFilters),
    [localFilters],
  );
  const hasChanges = useMemo(
    () => JSON.stringify(sanitizedLocalFilters) !== baselineSnapshot,
    [sanitizedLocalFilters, baselineSnapshot],
  );

  const handleLocalFilterChange = useCallback((
    field: any,
    operator: any,
    value: any,
    type: any,
    entityType: any,
    id: number,
    filterId: number,
    time_window?: string,
  ) => {
    setLocalFilters((prev: any) => {
      const newFilters = cloneDeep(prev);
      const group = newFilters.predicates[filterId];
      if (group && group.predicates[id]) {
        group.predicates[id] = {
          field,
          operator,
          value,
          type,
          entity_type: entityType,
          time_window,
        };
      }
      return newFilters;
    });
  }, []);

  const handleLocalAddCondition = useCallback((filterId?: number) => {
    setLocalFilters((prev: any) => {
      const newFilters = cloneDeep(prev);
      const group = newFilters.predicates?.[filterId!];
      if (group) {
        group.predicates.push({ ...EMPTY_CONDITION });
      }
      return newFilters;
    });
  }, []);

  const handleLocalRemoveCondition = useCallback((currentKey: number, filterId?: number) => {
    setLocalFilters((prev: any) => {
      const newFilters = cloneDeep(prev);
      const group = newFilters.predicates[filterId!];
      if (group) {
        group.predicates.splice(currentKey, 1);
        if (group.predicates.length === 0) {
          newFilters.predicates.splice(filterId!, 1);
        }
      }
      return newFilters;
    });
  }, []);

  const handleLocalAddGroup = useCallback(() => {
    setLocalFilters((prev: any) => {
      const newFilters = cloneDeep(prev);
      if (!newFilters.predicates) newFilters.predicates = [];
      newFilters.predicates.push(createEmptyGroup());
      return newFilters;
    });
  }, []);

  const handleClearAll = useCallback(() => {
    setLocalFilters((prev: any) => ensureEditableFilters({ query_type: prev?.query_type || "AND" }));
  }, []);

  const handleApplyChanges = () => {
    onApply(sanitizedLocalFilters);
    setOpened(false);
  };

  return (
    <Popover open={opened} onOpenChange={setOpened}>
      <PopoverTrigger asChild>
        <div>{target}</div>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align={getPopoverAlignment(position)}
        sideOffset={6}
        className="overflow-hidden rounded-xl border border-border bg-background p-0 shadow-popover"
        style={{ width: `min(${width}px, calc(100vw - 1.5rem))` }}
      >
        <div className="flex items-center justify-between border-b border-border bg-muted/30 px-2.5 py-1.5">
          <span className="rounded-md bg-background px-2 py-1 text-xs font-medium text-foreground">
            {getEntityLabel(entityRelation, undefined, "plural")}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground"
          >
            Clear
          </Button>
        </div>

        <div className="max-h-[420px] overflow-y-auto bg-background px-2.5 py-2">
          <div className="space-y-2">
            {localFilters?.predicates?.map((filter: any, index: number) => (
              <div key={`filter-group-${index}`} className="relative">
                <SignalFilterContainer
                  entityRelation={entityRelation}
                  selectedFilters={filter.predicates}
                  handleAdd={handleLocalAddCondition}
                  handleRemove={handleLocalRemoveCondition}
                  handleFilter={handleLocalFilterChange}
                  filterId={index}
                  className="px-0 py-0"
                />
                {localFilters?.predicates?.length === index + 1 ? null : (
                  <div className="my-1 flex items-center gap-2">
                    <div className="h-px flex-1 bg-border/70" />
                    <span className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
                      OR
                    </span>
                    <div className="h-px flex-1 bg-border/70" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="w-full pt-1">
            <button
              type="button"
              onClick={handleLocalAddGroup}
              className="inline-flex items-center rounded-md px-1 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <PlusIcon className="mr-1 h-3 w-3 flex-shrink-0" />
              Add group
            </button>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border bg-background px-2.5 py-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOpened(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleApplyChanges}
            disabled={!hasChanges}
            className="min-w-[96px]"
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SignalFilterPopover;
